import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthContext } from "./components/AuthProvider";
import { generateUgcIdeas, initializeProject, startAgentCompose } from "./lib/api";
import { trackMetaEvent } from "./lib/meta";
import { sendCAPIEvent } from "./lib/api";
import { getAdAttribution, captureAdAttribution } from "./lib/adAttribution";
import { useTheme } from "./hooks/useTheme";
import { saveCached, loadCached, clearCached } from "./lib/fileCache";
import "./UgcMobilePage.css";

/* ─── Constants ─── */

const DEFAULT_CHARACTERS = [
  { id: "julia", name: "Julia", thumb: "https://latted.b-cdn.net/misc/Julia.png", imageUrl: "https://latted.b-cdn.net/misc/Julia.png" },
  { id: "jess", name: "Jess", thumb: "https://latted.b-cdn.net/misc/Jess.png", imageUrl: "https://latted.b-cdn.net/misc/Jess.png" },
  { id: "jake", name: "Jake", thumb: "https://latted.b-cdn.net/misc/Jake.png", imageUrl: "https://latted.b-cdn.net/misc/Jake.png" },
  { id: "kim", name: "Kim", thumb: "https://latted.b-cdn.net/misc/Kim.png", imageUrl: "https://latted.b-cdn.net/misc/Kim.png" },
  { id: "roy", name: "Roy", thumb: "https://latted.b-cdn.net/misc/Roy.png", imageUrl: "https://latted.b-cdn.net/misc/Roy.png" },
  { id: "tate", name: "Tate", thumb: "https://latted.b-cdn.net/misc/Tate.png", imageUrl: "https://latted.b-cdn.net/misc/Tate.png" },
  { id: "clav", name: "Clav", thumb: "https://latted.b-cdn.net/misc/Clav.png", imageUrl: "https://latted.b-cdn.net/misc/Clav.png" },
  { id: "ashley", name: "Ashley", thumb: "https://latted.b-cdn.net/misc/Ashley.png", imageUrl: "https://latted.b-cdn.net/misc/Ashley.png" },
];

const TOTAL_SLIDES = 5; // character, product, description, options, review
const SLIDE_GENERATING = 5; // virtual 6th slide shown during/after generation
const PENDING_UGC_KEY = "latted_pending_ugc";

/* ─── Slide components ─── */

function SlideCharacter({ selectedCharacter, customCharacterFile, onSelectDefault, onUploadCustom }) {
  const fileRef = useRef(null);
  const customPreview = customCharacterFile ? URL.createObjectURL(customCharacterFile) : null;

  return (
    <div className="ugcm-slide">
      <h2 className="ugcm-slide-title">Pick your creator</h2>
      <p className="ugcm-slide-desc">Choose an AI persona or upload your own.</p>

      <div className="ugcm-char-grid">
        {DEFAULT_CHARACTERS.map((c) => (
          <button
            key={c.id}
            type="button"
            className={`ugcm-char-card ${selectedCharacter?.id === c.id && !customCharacterFile ? "ugcm-char-card--selected" : ""}`}
            onClick={() => onSelectDefault(c)}
          >
            <img src={c.thumb} alt={c.name} className="ugcm-char-img" />
            <span className="ugcm-char-name">{c.name}</span>
          </button>
        ))}

        <button
          type="button"
          className={`ugcm-char-card ugcm-char-card--upload ${customCharacterFile ? "ugcm-char-card--selected" : ""}`}
          onClick={() => fileRef.current?.click()}
        >
          {customPreview ? (
            <img src={customPreview} alt="Custom" className="ugcm-char-img" />
          ) : (
            <div className="ugcm-char-upload-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
            </div>
          )}
          <span className="ugcm-char-name">{customCharacterFile ? "Custom" : "Upload"}</span>
        </button>
      </div>

      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="ugcm-hidden-input"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onUploadCustom(f);
          e.target.value = "";
        }}
      />
    </div>
  );
}

function SlideProduct({ productFiles, onAdd, onRemove }) {
  const fileRef = useRef(null);

  return (
    <div className="ugcm-slide">
      <h2 className="ugcm-slide-title">Add your product</h2>
      <p className="ugcm-slide-desc">Upload photos of your product, logo, or packaging.</p>

      <div className="ugcm-product-grid">
        {productFiles.map((f, i) => (
          <div key={i} className="ugcm-product-card">
            <img src={URL.createObjectURL(f)} alt={f.name} className="ugcm-product-img" />
            <button type="button" className="ugcm-product-remove" onClick={() => onRemove(i)} aria-label="Remove">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
            </button>
          </div>
        ))}

        <button type="button" className="ugcm-product-add" onClick={() => fileRef.current?.click()}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          <span>Add photo</span>
        </button>
      </div>

      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        multiple
        className="ugcm-hidden-input"
        onChange={(e) => {
          if (e.target.files?.length) onAdd(Array.from(e.target.files));
          e.target.value = "";
        }}
      />
    </div>
  );
}

function SlideDescription({ description, onChange }) {
  return (
    <div className="ugcm-slide">
      <h2 className="ugcm-slide-title">Describe your product</h2>
      <p className="ugcm-slide-desc">Tell us about your product, audience, and what makes it special.</p>

      <textarea
        className="ugcm-textarea"
        value={description}
        onChange={(e) => onChange(e.target.value)}
        placeholder="e.g. Cal AI is an AI-powered calorie tracking app. Users take a photo of their food and it instantly shows calories and macros. Target audience: fitness enthusiasts aged 18-35..."
        rows={6}
        autoFocus
      />
    </div>
  );
}

function SlideOptions({ addCaptions, onToggleCaptions }) {
  return (
    <div className="ugcm-slide">
      <h2 className="ugcm-slide-title">Finishing touches</h2>
      <p className="ugcm-slide-desc">Choose how your video should be presented.</p>

      <label className="ugcm-toggle-row">
        <span className="ugcm-toggle-label">Add captions to videos</span>
        <button
          type="button"
          role="switch"
          aria-checked={addCaptions}
          className={`ugcm-toggle ${addCaptions ? "ugcm-toggle--on" : ""}`}
          onClick={onToggleCaptions}
        >
          <span className="ugcm-toggle-thumb" />
        </button>
      </label>
    </div>
  );
}

function SlideReview({ character, customCharacterFile, productFiles, description, addCaptions }) {
  const charName = customCharacterFile ? "Custom upload" : character?.name || "None";
  const charThumb = customCharacterFile ? URL.createObjectURL(customCharacterFile) : character?.thumb;

  return (
    <div className="ugcm-slide">
      <h2 className="ugcm-slide-title">Review & Generate</h2>
      <p className="ugcm-slide-desc">Make sure everything looks good before we start.</p>

      <div className="ugcm-review-list">
        <div className="ugcm-review-item">
          <span className="ugcm-review-label">Creator</span>
          <div className="ugcm-review-value">
            {charThumb && <img src={charThumb} alt="" className="ugcm-review-thumb" />}
            <span>{charName}</span>
          </div>
        </div>

        <div className="ugcm-review-item">
          <span className="ugcm-review-label">Products</span>
          <span className="ugcm-review-value">{productFiles.length} photo{productFiles.length !== 1 ? "s" : ""}</span>
        </div>

        <div className="ugcm-review-item">
          <span className="ugcm-review-label">Description</span>
          <span className="ugcm-review-value ugcm-review-value--desc">{description}</span>
        </div>

        <div className="ugcm-review-item">
          <span className="ugcm-review-label">Captions</span>
          <span className="ugcm-review-value">{addCaptions ? "Yes" : "No"}</span>
        </div>
      </div>
    </div>
  );
}

/* ─── Main Page ─── */

export default function UgcMobilePage() {
  const navigate = useNavigate();
  const { user, openAuthModal } = useAuthContext();
  const isDarkTheme = useTheme();

  const [slide, setSlide] = useState(0);
  const [selectedCharacter, setSelectedCharacter] = useState(null);
  const [customCharacterFile, setCustomCharacterFile] = useState(null);
  const [productFiles, setProductFiles] = useState([]);
  const [description, setDescription] = useState("");
  const [addCaptions, setAddCaptions] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [pendingSubmit, setPendingSubmit] = useState(false);
  const [kickoffStatus, setKickoffStatus] = useState("idle"); // idle | ideating | composing | done | error | skipped
  const [kickoffProjectId, setKickoffProjectId] = useState(null);
  const [oauthReturn, setOauthReturn] = useState(false); // shows "re-upload" banner after OAuth reload

  const logoSrc = isDarkTheme ? "/latted_white.png" : "/latted_black.png";

  // Ad attribution + pixel
  useEffect(() => {
    captureAdAttribution();
    const eventId = trackMetaEvent("ViewContent", {
      content_type: "ugc_mobile",
      content_name: "UGC Mobile Creator",
    });
    sendCAPIEvent("ViewContent", eventId, {
      content_type: "ugc_mobile",
      content_name: "UGC Mobile Creator",
    }, getAdAttribution()).catch(() => {});
  }, []);

  // Kick off the real UGC project generation. We await the full pipeline so
  // the confirmation screen can reflect the true state to the user.
  const kickoffUgcProject = useCallback(async () => {
    // Files are required for the UGC pipeline; after a Google-OAuth page
    // reload they're gone, so skip gracefully.
    if (productFiles.length === 0) {
      console.warn("UGC kickoff skipped: no product files (likely lost after OAuth redirect)");
      setKickoffStatus("skipped");
      return;
    }

    setKickoffStatus("ideating");
    try {
      const ideasResp = await generateUgcIdeas({
        description: description.trim(),
        numIdeas: 1,
        characterImageUrl: selectedCharacter?.imageUrl || null,
        characterName: selectedCharacter?.name || null,
        characterFile: customCharacterFile || null,
        productFiles,
      });

      const firstIdea = ideasResp?.ideas?.[0];
      if (!firstIdea) {
        console.error("UGC kickoff: no ideas returned", ideasResp);
        setKickoffStatus("error");
        return;
      }

      const script = firstIdea.detailed_script || firstIdea.script || firstIdea.concept || "";
      const captionLine = addCaptions ? "\n\nAdd captions to all videos with speech." : "";
      const fullPrompt =
        "Create a UGC-style product ad video. Use the ugc-video skill. " +
        "The character and product media are attached. Script:\n\n" +
        script + captionLine;

      const projectId = Math.random().toString(36).substring(2, 15);
      const files = customCharacterFile ? [customCharacterFile, ...productFiles] : [...productFiles];
      const attachmentUrls = selectedCharacter?.imageUrl ? [selectedCharacter.imageUrl] : [];

      setKickoffStatus("composing");
      await initializeProject(projectId, fullPrompt, "9:16");
      // Fire the agent run — it runs on the backend even if the user navigates away.
      startAgentCompose(projectId, fullPrompt, files, "pro", attachmentUrls, "ugc_ads_template").catch((err) => {
        console.error("startAgentCompose failed:", err);
      });

      setKickoffProjectId(projectId);
      setKickoffStatus("done");
    } catch (err) {
      console.error("UGC kickoff failed:", err);
      setKickoffStatus("error");
      setError("Failed to start your UGC project. Please try again.");
    }
  }, [description, selectedCharacter, customCharacterFile, productFiles, addCaptions]);

  // After submit (user is authenticated), kick off the UGC project in the background.
  const runGeneration = useCallback(() => {
    setIsSubmitting(true);
    setError(null);
    setSlide(SLIDE_GENERATING);

    const eventId = trackMetaEvent("InitiateCheckout", { content_type: "ugc_mobile" });
    sendCAPIEvent("InitiateCheckout", eventId, { content_type: "ugc_mobile" }, getAdAttribution()).catch(() => {});

    // Kick off the real UGC generation pipeline — runs in the background.
    // The fetch continues even after we navigate away from this page.
    kickoffUgcProject();

    setIsSubmitting(false);
  }, [kickoffUgcProject]);

  const handleViewProject = useCallback(() => {
    navigate(kickoffProjectId ? `/project/${kickoffProjectId}` : "/projects");
  }, [navigate, kickoffProjectId]);

  // After auth modal closes and user appears, auto-trigger generation
  useEffect(() => {
    if (pendingSubmit && user) {
      setPendingSubmit(false);
      runGeneration();
    }
  }, [pendingSubmit, user, runGeneration]);

  // Restore full wizard state from IndexedDB after OAuth redirect.
  const hasRestoredRef = useRef(false);
  useEffect(() => {
    if (hasRestoredRef.current || !user) return;
    const marker = sessionStorage.getItem(PENDING_UGC_KEY);
    if (!marker) return;
    hasRestoredRef.current = true;

    (async () => {
      try {
        const saved = await loadCached(PENDING_UGC_KEY);
        // Clear the marker + cache whether or not we get data back
        sessionStorage.removeItem(PENDING_UGC_KEY);
        await clearCached(PENDING_UGC_KEY).catch(() => {});

        if (!saved) return;

        if (saved.description) setDescription(saved.description);
        if (saved.addCaptions) setAddCaptions(saved.addCaptions);
        if (saved.selectedCharacterId) {
          const found = DEFAULT_CHARACTERS.find((c) => c.id === saved.selectedCharacterId);
          if (found) setSelectedCharacter(found);
        }
        if (saved.customCharacterFile) setCustomCharacterFile(saved.customCharacterFile);
        if (Array.isArray(saved.productFiles) && saved.productFiles.length) {
          setProductFiles(saved.productFiles);
        }

        // Auto-trigger generation now that we're signed in and have everything.
        setPendingSubmit(true);
      } catch (err) {
        console.error("Failed to restore cached wizard state:", err);
        setOauthReturn(true);
        setSlide(1);
      }
    })();
  }, [user]);

  const canAdvance = useCallback(() => {
    switch (slide) {
      case 0: return !!(selectedCharacter || customCharacterFile);
      case 1: return productFiles.length > 0;
      case 2: return description.trim().length > 10;
      case 3: return true;
      case 4: return true;
      default: return false;
    }
  }, [slide, selectedCharacter, customCharacterFile, productFiles, description]);

  const handleSelectDefault = useCallback((c) => {
    setSelectedCharacter(c);
    setCustomCharacterFile(null);
  }, []);

  const handleUploadCustom = useCallback((f) => {
    setCustomCharacterFile(f);
    setSelectedCharacter(null);
  }, []);

  const handleAddProducts = useCallback((files) => {
    setProductFiles((prev) => [...prev, ...files]);
  }, []);

  const handleRemoveProduct = useCallback((index) => {
    setProductFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const next = useCallback(() => {
    if (slide < TOTAL_SLIDES - 1 && canAdvance()) {
      setSlide((s) => s + 1);
      setError(null);
    }
  }, [slide, canAdvance]);

  const prev = useCallback(() => {
    if (slide > 0) {
      setSlide((s) => s - 1);
      setError(null);
    }
  }, [slide]);

  const handleSubmit = useCallback(async () => {
    if (!user) {
      // Persist the full wizard state (including File objects) so it survives
      // an OAuth redirect. IndexedDB stores File/Blob natively.
      try {
        await saveCached(PENDING_UGC_KEY, {
          description,
          addCaptions,
          selectedCharacterId: selectedCharacter?.id || null,
          productFiles,
          customCharacterFile,
        });
        // Also set a lightweight sessionStorage marker so App.js can route the
        // user back to /ugc/create after an OAuth redirect without waiting on
        // IndexedDB.
        sessionStorage.setItem(PENDING_UGC_KEY, "1");
      } catch (err) {
        console.error("Failed to cache wizard state:", err);
      }
      setPendingSubmit(true);
      openAuthModal();
      return;
    }
    runGeneration();
  }, [user, openAuthModal, runGeneration, description, addCaptions, selectedCharacter, productFiles, customCharacterFile]);

  const isLastSlide = slide === TOTAL_SLIDES - 1;
  const isGenerating = slide === SLIDE_GENERATING;

  return (
    <div className="ugcm-page">
      {/* ─── Header ─── */}
      <header className="ugcm-header">
        <img src={logoSrc} alt="Latted" className="ugcm-header-logo" />
        <div className="ugcm-progress">
          {Array.from({ length: TOTAL_SLIDES }, (_, i) => (
            <div key={i} className={`ugcm-progress-dot ${i <= slide ? "ugcm-progress-dot--active" : ""} ${i === slide ? "ugcm-progress-dot--current" : ""}`} />
          ))}
        </div>
        <span className="ugcm-header-step">{slide + 1}/{TOTAL_SLIDES}</span>
      </header>

      {oauthReturn && productFiles.length === 0 && (
        <div className="ugcm-oauth-banner">
          <span>Welcome back! Please re-attach your product photos to finish.</span>
        </div>
      )}

      {/* ─── Slides ─── */}
      <main className="ugcm-main">
        <div className={`ugcm-slides-panel ${slide === 0 ? "ugcm-slides-panel--active" : ""}`}>
          <SlideCharacter
            selectedCharacter={selectedCharacter}
            customCharacterFile={customCharacterFile}
            onSelectDefault={handleSelectDefault}
            onUploadCustom={handleUploadCustom}
          />
        </div>
        <div className={`ugcm-slides-panel ${slide === 1 ? "ugcm-slides-panel--active" : ""}`}>
          <SlideProduct
            productFiles={productFiles}
            onAdd={handleAddProducts}
            onRemove={handleRemoveProduct}
          />
        </div>
        <div className={`ugcm-slides-panel ${slide === 2 ? "ugcm-slides-panel--active" : ""}`}>
          <SlideDescription description={description} onChange={setDescription} />
        </div>
        <div className={`ugcm-slides-panel ${slide === 3 ? "ugcm-slides-panel--active" : ""}`}>
          <SlideOptions
            addCaptions={addCaptions}
            onToggleCaptions={() => setAddCaptions((v) => !v)}
          />
        </div>
        <div className={`ugcm-slides-panel ${slide === 4 ? "ugcm-slides-panel--active" : ""}`}>
          <SlideReview
            character={selectedCharacter}
            customCharacterFile={customCharacterFile}
            productFiles={productFiles}
            description={description}
            addCaptions={addCaptions}
          />
        </div>
        <div className={`ugcm-slides-panel ${slide === SLIDE_GENERATING ? "ugcm-slides-panel--active" : ""}`}>
          <div className="ugcm-slide ugcm-slide--generating">
            {kickoffStatus === "error" ? (
              <>
                <div className="ugcm-gen-icon ugcm-gen-icon--error">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                </div>
                <h2 className="ugcm-slide-title">Couldn't start your video</h2>
                <p className="ugcm-slide-desc">
                  Something went wrong kicking off your UGC project. Return to your projects and try again when you're ready.
                </p>
              </>
            ) : kickoffStatus === "done" || kickoffStatus === "skipped" ? (
              <>
                <div className="ugcm-gen-icon ugcm-gen-icon--success">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                </div>
                <h2 className="ugcm-slide-title">
                  {kickoffStatus === "done" ? "Your first video is on its way!" : "You're all set!"}
                </h2>
                <p className="ugcm-slide-desc">
                  {kickoffStatus === "done"
                    ? "We've kicked off your first UGC project — it's generating in the background right now."
                    : "We've saved your setup."}
                </p>
              </>
            ) : (
              <>
                <div className="ugcm-gen-icon">
                  <span className="ugcm-gen-spinner" />
                </div>
                <h2 className="ugcm-slide-title">
                  {kickoffStatus === "composing" ? "Starting your project..." : "Brainstorming your first video..."}
                </h2>
                <p className="ugcm-slide-desc">
                  {kickoffStatus === "composing"
                    ? "Initializing your UGC project and kicking off the Composer agent."
                    : "Our AI is turning your product into a viral video script. This usually takes 15–30 seconds."}
                </p>
              </>
            )}

            <button
              type="button"
              className="ugcm-btn-next ugcm-btn-next--submit ugcm-gen-cta"
              onClick={handleViewProject}
            >
              {kickoffProjectId ? "View your video project" : "View projects"}
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
            </button>
          </div>
        </div>
      </main>

      {/* ─── Error ─── */}
      {error && (
        <div className="ugcm-error">
          <p>{error}</p>
          <button type="button" onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      {/* ─── Bottom nav (hidden during generation) ─── */}
      {!isGenerating && (
        <footer className="ugcm-footer">
          {slide > 0 ? (
            <button type="button" className="ugcm-btn-back" onClick={prev}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
              Back
            </button>
          ) : (
            <div />
          )}

          {isLastSlide ? (
            <button
              type="button"
              className="ugcm-btn-next ugcm-btn-next--submit"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {user ? "Generate Ideas" : "Sign Up & Generate"}
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
            </button>
          ) : (
            <button
              type="button"
              className="ugcm-btn-next"
              onClick={next}
              disabled={!canAdvance()}
            >
              Next
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
            </button>
          )}
        </footer>
      )}
    </div>
  );
}
